<template>
  <div class="modern-date-select">
    <ModernSelect :disabled="disabled" :options="options" v-model="value.selectedRange"/>
    <transition name="fade">
      <div class="modern-date-select__custom" v-if="selectedRange === 'Custom'">
        <modern-date-picker
          :disabled="disabled"
          icon="calendar-today"
          v-model="customRange"
          :min-date="minDate"
          :max-date="maxDate"
          :mobile-native="false"
          :dateFormatter="dateFormatter"
          range/>
      </div>
    </transition>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { fromArray, getRange } from '@/utils/range';
import { RANGE } from '@/const/constants';
import ModernSelect from '@/components/ModernSelect.vue';
import ModernDatePicker from '@/components/ModernDatepicker.vue';

export default {
  name: 'ModernDateSelect',
  components: { ModernSelect, ModernDatePicker },
  props: {
    minDate: {
      type: Date,
      default: () => null,
    },
    maxDate: {
      type: Date,
      default: () => null,
    },
    value: {
      type: Object,
    },
    defaultOption: {
      type: String,
      default: () => RANGE.LAST_30_DAYS,
    },
    options: {
      type: Array,
      default: () => [
        { value: RANGE.CONTRACTOR_TIME, label: 'All time' },
        { value: RANGE.LAST_365_DAYS, label: 'Last 365 days' },
        { value: RANGE.LAST_30_DAYS, label: 'Last 30 days' },
        { value: RANGE.LAST_7_DAYS, label: 'Last 7 days' },
        { value: RANGE.THIS_MONTH, label: 'This month' },
        { value: RANGE.LAST_MONTH, label: 'Last month' },
        { value: RANGE.LAST_2_MONTHS, label: 'Last 2 months' },
        { value: RANGE.LAST_3_MONTHS, label: 'Last 3 months' },
        { value: RANGE.LAST_6_MONTHS, label: 'Last 6 months' },
        { value: RANGE.LAST_WEEK, label: 'Last week' },
        { value: RANGE.YESTERDAY, label: 'Yesterday' },
        { value: RANGE.TODAY, label: 'Today' },
        { value: RANGE.CUSTOM, label: 'Custom' },
      ],
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
  },

  data() {
    return {
      customRange: this.getDefaultCustomRange(),
      minData: this.minData,
    };
  },

  methods: {
    resetCustomRange() {
      this.customRange = this.getDefaultCustomRange();
    },
    getDefaultCustomRange() {
      const { from, to } = getRange(RANGE.LAST_3_MONTH);
      const start = this.minDate && from < this.minDate
        ? this.minDate
        : from;
      return [start, to];
    },
    setCustomRange(customRange) {
      this.customRange = [customRange.from, customRange.to];
    },

    dateFormatter(date) {
      return `${dayjs(date[0]).format('MM/DD/YYYY')} - ${dayjs(date[1]).format('MM/DD/YYYY')}`;
    },
  },

  computed: {
    selectedRange() { return this.value ? this.value.selectedRange : this.defaultOption; },
  },

  watch: {
    selectedRange(val) {
      let range = getRange(val);
      if (val === RANGE.CUSTOM) {
        range = fromArray(this.customRange);
      }
      if (val === RANGE.CONTRACTOR_TIME) {
        range = getRange(val, this.minDate);
      }
      this.$emit('input', { selectedRange: val, ...range });
    },
    customRange(val) {
      if (this.selectedRange !== RANGE.CUSTOM) {
        return;
      }
      let [from, to] = val;
      if (this.minDate && from < this.minDate) {
        from = this.minDate;
      }
      if (this.maxDate && to > this.maxDate) {
        to = this.maxDate;
      }
      this.$emit('input', {
        selectedRange: RANGE.CUSTOM,
        ...fromArray([from, to]),
      });
    },
  },
};
</script>

<style scoped lang="stylus">
.datepicker.control
  width 100%
</style>
